// Extend Window interface for Teams SDK
declare global {
  interface Window {
    microsoftTeams?: {
      app?: {
        initialize?: () => Promise<void>;
      };
      initialize?: () => void;
    };
  }
}

/**
 * Detects if the application is running within Microsoft Teams context.
 * Uses multiple detection strategies for reliability across different Teams versions and environments.
 *
 * Detection strategies (in order of reliability):
 * 1. Check if Teams SDK is available and initialized
 * 2. Check window.name for Teams-specific iframe names
 * 3. Check for Teams native interface (mobile/desktop)
 * 4. Check User-Agent for Teams client
 * 5. Check URL parameters for Teams context
 * 6. Check window.origin for Teams/Microsoft domains (including teams.microsoft.com, *.teams.microsoft.com, teams.live.com, and *.sharepoint.com)
 *
 * @returns {boolean} True if running in Teams, false otherwise
 */
export const isInTeams = (): boolean => {
  // Strategy 1: Check if microsoftTeams SDK is available
  if (window.microsoftTeams) {
    return true;
  }

  // Strategy 2: Check window.name (most reliable for iframe detection)
  // Teams sets specific window names when hosting apps
  const isTeamsIframe =
    window.name === 'embedded-page-container' ||
    window.name === 'extension-tab-frame' ||
    window.name === 'embedded-page-container-v2';

  if (isTeamsIframe) {
    return true;
  }

  // Strategy 3: Check for Teams native interface (mobile/desktop apps)
  // When running in native Teams client (not web)
  if (window.parent === window.self && window.nativeInterface) {
    return true;
  }

  // Strategy 4: Check User-Agent for Teams client
  // Fallback for cases where window.name is not set
  const userAgent = navigator.userAgent || '';
  if (userAgent.includes('Teams/')) {
    return true;
  }

  // Strategy 5: Check URL parameters for Teams context
  // Some Teams versions pass context via URL
  const urlParameters = new URLSearchParams(window.location.search);
  if (urlParameters.has('teams') || urlParameters.has('context')) {
    return true;
  }

  // Strategy 6: Check window.origin for Teams/Microsoft domains
  // Teams apps are typically hosted on teams.microsoft.com or *.teams.microsoft.com
  const origin =
    window.location.origin ||
    `${window.location.protocol}//${window.location.host}`;
  if (
    origin.includes('teams.microsoft.com') ||
    origin.includes('.teams.microsoft.com') ||
    origin.includes('teams.live.com') ||
    origin.includes('.sharepoint.com')
  ) {
    return true;
  }

  return false;
};
